import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { object, SchemaOf, string } from 'yup'
import { Errors } from 'config'
import { useMapTwoFactorError } from 'shared/hooks/use-map-two-factor-error'
import type { FormProps } from '../Form'

export const TWO_FACTOR_CODE_LENGTH = 6

export enum TwoFactorCodeFormFields {
  Code = 'code',
}

export interface TwoFactorCodeFormValues {
  [TwoFactorCodeFormFields.Code]: string
}

export const toDigits = (text: string) => text.replace(/\D/g, '')

// With the box's digit selected the typed one replaces it, otherwise the box
// holds both, e.g. "35" after typing 5 next to 3
export const getTypedDigits = (text: string, boxDigit: string) => {
  const digits = toDigits(text)
  return boxDigit && digits.length === 2 ? digits.replace(boxDigit, '') : digits
}

// A whole code, e.g. pasted or filled in by the phone, replaces the entered one.
// Fewer digits overwrite the code from the box on.
export const enterDigits = (code: string, index: number, digits: string) => {
  if (digits.length >= TWO_FACTOR_CODE_LENGTH)
    return digits.slice(0, TWO_FACTOR_CODE_LENGTH)

  const entered =
    code.slice(0, index) + digits + code.slice(index + digits.length)
  return entered.slice(0, TWO_FACTOR_CODE_LENGTH)
}

export const removeDigit = (code: string, index: number) =>
  code.slice(0, index) + code.slice(index + 1)

const INVALID_CODE_ERRORS: string[] = [
  Errors.InvalidVerificationCode,
  Errors.MissingVerificationCode,
]

// An invalid code is shown on the field itself, any other error above the form
export const useTwoFactorCodeForm = (
  onSubmit: (code: string) => void | Promise<void>,
  error: string | null
) => {
  const { t } = useTranslation()
  const { mapError } = useMapTwoFactorError()
  const schema: SchemaOf<TwoFactorCodeFormValues> = object().shape({
    [TwoFactorCodeFormFields.Code]: string()
      .required(t('validation.required'))
      .matches(
        new RegExp(`^\\d{${TWO_FACTOR_CODE_LENGTH}}$`),
        t('validation.twoFactorCode', { length: TWO_FACTOR_CODE_LENGTH })
      ),
  })
  const methods = useForm<TwoFactorCodeFormValues>({
    defaultValues: { [TwoFactorCodeFormFields.Code]: '' },
    resolver: yupResolver(schema),
  })
  const isInvalidCodeError = !!error && INVALID_CODE_ERRORS.includes(error)

  useEffect(() => {
    if (!error || !isInvalidCodeError) return
    methods.setError(TwoFactorCodeFormFields.Code, {
      message: mapError(error),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const formProps: FormProps<TwoFactorCodeFormValues> = {
    ...methods,
    onSubmit: values => onSubmit(values[TwoFactorCodeFormFields.Code]),
  }

  return {
    formProps,
    formError: error && !isInvalidCodeError ? mapError(error) : null,
  }
}
